import { Box, Button, Stack, Typography, type ButtonProps } from "@mui/material";
import type { FC, ReactNode } from "react";
import {
  CC,
  ccDangerButtonSx,
  ccGhostButtonSx,
  ccPrimaryButtonSx,
  ccSoftButtonSx,
} from "./call-center-ui.theme";

export const CcEyebrow: FC<{ children: ReactNode; light?: boolean }> = ({
  children,
  light,
}) => (
  <Typography
    component="p"
    sx={{
      m: 0,
      fontFamily: CC.font,
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color: light ? "rgba(255,252,247,0.62)" : CC.inkMuted,
    }}
  >
    {children}
  </Typography>
);

export const CcButton: FC<
  ButtonProps & { kind?: "primary" | "ghost" | "soft" | "danger" }
> = ({ kind = "primary", sx, ...props }) => {
  const kindSx =
    kind === "ghost"
      ? ccGhostButtonSx
      : kind === "soft"
        ? ccSoftButtonSx
        : kind === "danger"
          ? ccDangerButtonSx
          : ccPrimaryButtonSx;
  return (
    <Button
      disableElevation
      variant={kind === "ghost" ? "outlined" : "contained"}
      sx={{ ...kindSx, ...((sx as object) ?? {}) }}
      {...props}
    />
  );
};

export const CcStageCard: FC<{ children: ReactNode; wide?: boolean }> = ({
  children,
  wide,
}) => (
  <Box
    sx={{
      width: "min(100%, " + (wide ? "640px" : "460px") + ")",
      bgcolor: CC.paper,
      color: CC.ink,
      borderRadius: `${CC.radius}px`,
      boxShadow: CC.shadow,
      p: { xs: 3, md: 4.5 },
    }}
  >
    {children}
  </Box>
);

export const CcProgress: FC<{ value: number; max: number }> = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <Box
      sx={{
        height: 6,
        borderRadius: 99,
        bgcolor: "rgba(5,32,76,0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: `${pct}%`,
          height: "100%",
          borderRadius: 99,
          bgcolor: pct >= 100 ? CC.success : CC.ink,
          transition: "width 400ms ease",
        }}
      />
    </Box>
  );
};

export const CcChip: FC<{ children: ReactNode; tone?: "ink" | "gold" | "warn" }> = ({
  children,
  tone = "ink",
}) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 1.1,
      py: 0.35,
      borderRadius: 99,
      fontFamily: CC.font,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.02em",
      bgcolor:
        tone === "gold"
          ? CC.goldSoft
          : tone === "warn"
            ? CC.distressSoft
            : "rgba(5,32,76,0.06)",
      color:
        tone === "gold" ? CC.gold : tone === "warn" ? CC.distress : CC.ink,
    }}
  >
    {children}
  </Box>
);

export const CcTrackChip: FC<{ isGroup: boolean }> = ({ isGroup }) => (
  <CcChip tone={isGroup ? "gold" : "ink"}>{isGroup ? "קבוצה" : "פרטני"}</CcChip>
);

export const CcPanel: FC<{
  children: ReactNode;
  sx?: object;
  "data-tour"?: string;
}> = ({ children, sx, "data-tour": dataTour }) => (
  <Box
    data-tour={dataTour}
    sx={{
      bgcolor: CC.paper,
      border: `1px solid ${CC.line}`,
      borderRadius: `${CC.radius}px`,
      boxShadow: CC.shadowSoft,
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const CcEmpty: FC<{ title: string; body: string }> = ({ title, body }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    sx={{ py: 8, px: 3, textAlign: "center" }}
    gap={1}
  >
    <Typography sx={{ fontFamily: CC.font, fontWeight: 700, fontSize: 20, color: CC.ink }}>
      {title}
    </Typography>
    <Typography sx={{ fontFamily: CC.font, color: CC.inkMuted, maxWidth: 420 }}>
      {body}
    </Typography>
  </Stack>
);

export const CcPills = <T extends string>({
  items,
  value,
  onChange,
  variant = "canvas",
}: {
  items: { value: T; label: string; badge?: number }[];
  value: T;
  onChange: (value: T) => void;
  variant?: "canvas" | "navy";
}) => (
  <Box
    sx={{
      display: "inline-flex",
      gap: 0.5,
      p: 0.5,
      maxWidth: "100%",
      flexWrap: "wrap",
      bgcolor: variant === "navy" ? "rgba(255,252,247,0.10)" : "rgba(5,32,76,0.05)",
      borderRadius: 99,
    }}
  >
    {items.map((item) => {
      const selected = value === item.value;
      const navy = variant === "navy";
      return (
        <Box
          key={item.value}
          component="button"
          type="button"
          onClick={() => onChange(item.value)}
          sx={{
            appearance: "none",
            border: 0,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.75,
            py: 0.85,
            borderRadius: 99,
            fontFamily: CC.font,
            fontSize: 13.5,
            fontWeight: 600,
            color: selected
              ? navy
                ? CC.ink
                : CC.paper
              : navy
                ? "rgba(255,252,247,0.72)"
                : CC.inkSoft,
            bgcolor: selected
              ? navy
                ? CC.paper
                : CC.ink
              : "transparent",
            boxShadow: selected ? CC.shadowSoft : "none",
            transition: "background-color 160ms ease, color 160ms ease",
            "&:hover": {
              bgcolor: selected
                ? navy
                  ? CC.paper
                  : CC.ink
                : navy
                  ? "rgba(255,252,247,0.08)"
                  : "rgba(5,32,76,0.08)",
            },
          }}
        >
          {item.label}
          {item.badge ? (
            <Box
              component="span"
              sx={{
                minWidth: 18,
                height: 18,
                px: 0.5,
                borderRadius: 99,
                display: "inline-grid",
                placeItems: "center",
                fontSize: 11,
                bgcolor: selected
                  ? navy
                    ? CC.distress
                    : "rgba(255,252,247,0.18)"
                  : CC.distress,
                color: CC.paper,
              }}
            >
              {item.badge}
            </Box>
          ) : null}
        </Box>
      );
    })}
  </Box>
);

export const tableFrameSx = {
  height: "100%",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  bgcolor: CC.paper,
  border: `1px solid ${CC.line}`,
  borderRadius: `${CC.radius}px`,
  boxShadow: CC.shadowSoft,
  px: 1.5,
  pt: 1.25,
  pb: 1,
  "& .titles-container .title": {
    fontFamily: `${CC.font} !important`,
    color: `${CC.ink} !important`,
    fontSize: "1.05rem !important",
    fontWeight: "700 !important",
    letterSpacing: "-0.02em",
  },
  "& .titles-container .divider": {
    color: `${CC.inkMuted} !important`,
  },
  "& .data-table-sub-container": {
    backgroundColor: `${CC.paper} !important`,
  },
  "& .MuiTable-root": {
    backgroundColor: CC.paper,
  },
  "& .MuiTableCell-root": {
    fontFamily: CC.font,
    color: CC.ink,
    backgroundColor: "transparent",
    borderColor: `${CC.line} !important`,
  },
  "& .table-head, & .table-head .table-row, & .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: `${CC.paper} !important`,
    boxShadow: "none !important",
  },
  "& .table-header-title": {
    color: `${CC.inkMuted} !important`,
    fontFamily: `${CC.font} !important`,
    fontWeight: "600 !important",
    fontSize: "12px !important",
    letterSpacing: "0.08em",
  },
  "& .table-body .table-row": {
    backgroundColor: CC.paper,
    "&:hover": {
      backgroundColor: "rgba(5,32,76,0.035)",
    },
  },
  "& .MuiButton-containedPrimary": {
    bgcolor: `${CC.ink} !important`,
    color: `${CC.paper} !important`,
    boxShadow: "none !important",
    fontFamily: `${CC.font} !important`,
    textTransform: "none !important",
    borderRadius: `${CC.radiusSm}px !important`,
    "&:hover": { bgcolor: "#0A2E63 !important" },
  },
  "& .MuiButton-outlined, & .MuiButton-text": {
    fontFamily: `${CC.font} !important`,
    textTransform: "none !important",
    color: `${CC.ink} !important`,
    borderColor: `${CC.lineStrong} !important`,
  },
          "& .MuiCheckbox-root.Mui-checked": {
            color: `${CC.ink} !important`,
          },
  "& .MuiTablePagination-root, & .MuiTablePagination-root *": {
    fontFamily: `${CC.font} !important`,
    color: `${CC.inkMuted} !important`,
  },
  "& .whatsapp-button + span, & .cell-content": {
    fontFamily: CC.font,
    color: CC.ink,
  },
} as const;

